import { CLASSES, ARIAS, ATTRIBUTES, SELECTORS, SETTINGS } from '../../utils/constants';
import initSearch from './search';

// Open states work by removing the offscreen translate class from the markup
// rather than adding an overriding one, so Tailwind's utility order in the
// compiled CSS can never flip which class wins. Closed drawer/panels also
// carry the inert attribute so offscreen controls leave the tab order.
const lockScroll = (scrollParentEl) => {
  if (!scrollParentEl) return;
  scrollParentEl.scrollTop = 0;
  scrollParentEl.classList.remove(CLASSES.SCROLLABLE);
  scrollParentEl.classList.add(CLASSES.OVERFLOW_HIDDEN);
};

const unlockScroll = (scrollParentEl) => {
  if (!scrollParentEl) return;
  scrollParentEl.classList.remove(CLASSES.OVERFLOW_HIDDEN);
  scrollParentEl.classList.add(CLASSES.SCROLLABLE);
};

const resetPanels = (drawerEl) => {
  const panelEls = drawerEl.querySelectorAll(SELECTORS.NAV_PANEL);
  panelEls.forEach(panelEl => {
    panelEl.classList.add(CLASSES.TRANSLATE_X_100);
    panelEl.setAttribute(ATTRIBUTES.INERT, '');
    unlockScroll(panelEl);
    panelEl.scrollTop = 0;
  });
  const expandedToggleSelector = SELECTORS.PANEL_OPEN + '[aria-expanded="true"]';
  const openToggleEls = drawerEl.querySelectorAll(expandedToggleSelector);
  openToggleEls.forEach(toggleEl => toggleEl.setAttribute(ARIAS.EXPANDED, 'false'));
  unlockScroll(drawerEl);
  drawerEl.scrollTop = 0;
};

const setDrawerState = (drawerEl, toggleEl, isOpen) => {
  drawerEl.classList.toggle(CLASSES.TRANSLATE_X_LEFT_100, !isOpen);
  toggleEl.setAttribute(ARIAS.EXPANDED, isOpen);
  if (isOpen) {
    drawerEl.removeAttribute(ATTRIBUTES.INERT);
    resetPanels(drawerEl);
    return;
  }
  drawerEl.setAttribute(ATTRIBUTES.INERT, '');
  toggleEl.focus();
  setTimeout(() => resetPanels(drawerEl), SETTINGS.NAV_TRANSITION_DURATION);
};

const openPanel = (buttonEl) => {
  const itemEl = buttonEl.closest('li');
  if (!itemEl) return;
  const panelEl = itemEl.querySelector(SELECTORS.NAV_PANEL);
  if (!panelEl) return;
  panelEl.classList.remove(CLASSES.TRANSLATE_X_100);
  panelEl.removeAttribute(ATTRIBUTES.INERT);
  buttonEl.setAttribute(ARIAS.EXPANDED, 'true');
  lockScroll(panelEl.parentElement.closest(SELECTORS.NAV_SCROLL_PARENT));
};

const closePanel = (panelEl) => {
  panelEl.classList.add(CLASSES.TRANSLATE_X_100);
  panelEl.setAttribute(ATTRIBUTES.INERT, '');
  const itemEl = panelEl.closest('li');
  const openToggleEl = itemEl ? itemEl.querySelector(SELECTORS.PANEL_OPEN) : null;
  if (openToggleEl) {
    openToggleEl.setAttribute(ARIAS.EXPANDED, 'false');
    openToggleEl.focus();
  }
  unlockScroll(panelEl.parentElement.closest(SELECTORS.NAV_SCROLL_PARENT));
};

const closeDeepestPanel = (drawerEl) => {
  const openPanelSelector = SELECTORS.NAV_PANEL + ':not(.' + CLASSES.TRANSLATE_X_100 + ')';
  const openPanelEls = drawerEl.querySelectorAll(openPanelSelector);
  if (!openPanelEls.length) return false;
  closePanel(openPanelEls[openPanelEls.length - 1]);
  return true;
};

const bindDrawerEvents = (drawerEl, toggleEl) => {
  drawerEl.addEventListener('click', (evt) => {
    const openButtonEl = evt.target.closest(SELECTORS.PANEL_OPEN);
    if (openButtonEl) {
      openPanel(openButtonEl);
      return;
    }
    const backButtonEl = evt.target.closest(SELECTORS.PANEL_BACK);
    if (backButtonEl) {
      const panelEl = backButtonEl.closest(SELECTORS.NAV_PANEL);
      if (panelEl) closePanel(panelEl);
      return;
    }
    const closeButtonEl = evt.target.closest(SELECTORS.NAV_CLOSE);
    if (closeButtonEl) setDrawerState(drawerEl, toggleEl, false);
  });
  document.addEventListener('keydown', (evt) => {
    if (evt.key !== 'Escape') return;
    if (drawerEl.classList.contains(CLASSES.TRANSLATE_X_LEFT_100)) return;
    if (closeDeepestPanel(drawerEl)) return;
    setDrawerState(drawerEl, toggleEl, false);
  });
};

// WCAG 1.4.13: Escape dismisses an open hover mega menu without moving the
// pointer; re-entering the item by pointer or keyboard re-enables it.
const initMegaMenu = (headerEl) => {
  const navParentEls = headerEl.querySelectorAll(SELECTORS.NAV_PARENT);
  if (!navParentEls.length) return;
  const clearDismissed = () => headerEl.removeAttribute(ATTRIBUTES.MEGA_DISMISSED);
  navParentEls.forEach(navParentEl => {
    navParentEl.addEventListener('mouseleave', clearDismissed);
    navParentEl.addEventListener('focusin', clearDismissed);
  });
  document.addEventListener('keydown', (evt) => {
    if (evt.key !== 'Escape') return;
    const activeParentSelector = SELECTORS.NAV_PARENT + ':hover, ' + SELECTORS.NAV_PARENT + ':focus-within';
    if (!headerEl.querySelector(activeParentSelector)) return;
    headerEl.setAttribute(ATTRIBUTES.MEGA_DISMISSED, '');
  });
};

const initHeader = () => {
  const headerEl = document.querySelector(SELECTORS.HEADER);
  if (!headerEl || headerEl.dataset.headerBound) return;
  headerEl.dataset.headerBound = 'true';
  const toggleEl = headerEl.querySelector(SELECTORS.NAV_TOGGLE);
  const drawerEl = headerEl.querySelector(SELECTORS.MOBILE_NAV);
  if (toggleEl && drawerEl) {
    toggleEl.addEventListener('click', () => {
      const isOpen = toggleEl.getAttribute(ARIAS.EXPANDED) !== 'true';
      setDrawerState(drawerEl, toggleEl, isOpen);
    });
    bindDrawerEvents(drawerEl, toggleEl);
  }
  initMegaMenu(headerEl);
  initSearch();
};

export default initHeader;
